import { Component, Inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material';
import {
  AdminContactDepartment,
  AdminContactPerson,
  ContactPersonFormDialogData,
  ContactPersonRequestDTO
} from '@app/model/contact.model';
import { environment } from '@app/../environments/environment';
import { ContactManagementDataService } from '@app/services/contact-management-data.service';
import { ToastrService } from 'ngx-toastr';
import { catchError, finalize, switchMap, take } from 'rxjs/operators';
import { of, throwError } from 'rxjs';

interface PersonLanguage {
  code: string;
  label: string;
}

@Component({
  selector: 'app-contact-person-form',
  templateUrl: './contact-person-form.component.html',
  styleUrls: ['./contact-person-form.component.scss']
})
export class ContactPersonFormComponent implements OnInit {
  isSaving = false;
  selectedLanguageIndex = 0;

  selectedImageFile: File | null = null;
  imagePreviewUrl = '';

  languages: PersonLanguage[] = [
    { code: 'vi', label: 'Tiếng Việt' },
    { code: 'en', label: 'English' },
    { code: 'zh', label: '中文' }
  ];

  languageForms: { [code: string]: FormGroup } = {
    vi: this.createLanguageForm(),
    en: this.createLanguageForm(),
    zh: this.createLanguageForm()
  };

  settingsForm = new FormGroup({
    departmentId: new FormControl('', [Validators.required]),
    fullName: new FormControl('', [Validators.required, Validators.maxLength(200)]),
    email: new FormControl('', [Validators.email, Validators.maxLength(200)]),
    phone: new FormControl('', [Validators.maxLength(10), Validators.pattern(/^[0-9]+$/)]),
    directPhone: new FormControl('', [Validators.maxLength(10), Validators.pattern(/^[0-9]+$/)]),
    zalo: new FormControl('', [Validators.maxLength(50)]),
    languagesSpoken: new FormControl('', [Validators.maxLength(200)]),
    sortOrder: new FormControl(0, [Validators.required, Validators.min(0)]),
    isHighlight: new FormControl(false),
    isActive: new FormControl(true)
  });

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: ContactPersonFormDialogData,
    private dialogRef: MatDialogRef<ContactPersonFormComponent>,
    private contactManagementDataService: ContactManagementDataService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    if (this.data.mode === 'edit' && this.data.person) {
      this.patchForm(this.data.person);
    }
  }

  get modalTitle(): string {
    return this.data.mode === 'edit' ? 'Sửa nhân sự' : 'Thêm nhân sự';
  }

  get submitLabel(): string {
    if (this.isSaving) {
      return 'Đang lưu...';
    }
    return this.data.mode === 'edit' ? 'Lưu thay đổi' : 'Tạo nhân sự';
  }

  get departments(): AdminContactDepartment[] {
    return this.data.departments || [];
  }

  closeModal(): void {
    if (this.isSaving) {
      return;
    }
    this.dialogRef.close();
  }

  onPhoneKeypress(event: KeyboardEvent): void {
    const charCode = event.which ? event.which : event.keyCode;
    if (charCode >= 48 && charCode <= 57) {
      return;
    }
    if (charCode === 0 || charCode === 8 || charCode === 9 || charCode === 13) {
      return;
    }
    event.preventDefault();
  }

  onImageSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || !input.files.length) {
      return;
    }
    const file = input.files[0];
    if (file.type.indexOf('image/') !== 0) {
      this.toastr.error('Vui lòng chọn đúng định dạng hình ảnh');
      input.value = '';
      return;
    }
    this.selectedImageFile = file;
    const reader = new FileReader();
    reader.onload = () => {
      this.imagePreviewUrl = typeof reader.result === 'string' ? reader.result : '';
    };
    reader.readAsDataURL(file);
  }

  savePerson(): void {
    if (this.isSaving) {
      return;
    }

    if (this.settingsForm.invalid) {
      this.settingsForm.markAllAsTouched();
      this.toastr.error('Vui lòng nhập đầy đủ thông tin thiết lập');
      return;
    }

    const vi = this.languageForms.vi.value;
    const en = this.languageForms.en.value;
    const zh = this.languageForms.zh.value;
    const settings = this.settingsForm.value;

    const payload: ContactPersonRequestDTO = {
      department_id: settings.departmentId,
      full_name: (settings.fullName || '').trim(),
      position: (vi.position || '').trim(),
      position_translations_vi: (vi.position || '').trim(),
      position_translations_en: (en.position || '').trim(),
      position_translations_zh: (zh.position || '').trim(),
      area_covered: (vi.areaCovered || '').trim(),
      area_covered_translations_vi: (vi.areaCovered || '').trim(),
      area_covered_translations_en: (en.areaCovered || '').trim(),
      area_covered_translations_zh: (zh.areaCovered || '').trim(),
      bio: (vi.bio || '').trim() || null,
      bio_translations_vi: (vi.bio || '').trim() || null,
      bio_translations_en: (en.bio || '').trim() || null,
      bio_translations_zh: (zh.bio || '').trim() || null,
      email: (settings.email || '').trim() || null,
      phone: (settings.phone || '').trim() || null,
      direct_phone: (settings.directPhone || '').trim() || null,
      zalo: (settings.zalo || '').trim() || null,
      languages: (settings.languagesSpoken || '').trim() || null,
      sort_order: Number(settings.sortOrder) || 0,
      is_highlight: settings.isHighlight === true,
      is_active: settings.isActive === true
    };

    this.submitPerson(payload);
  }

  private createLanguageForm(): FormGroup {
    return new FormGroup({
      position: new FormControl(''),
      areaCovered: new FormControl(''),
      bio: new FormControl('')
    });
  }

  private patchForm(person: AdminContactPerson): void {
    this.settingsForm.patchValue({
      departmentId: person.department_id,
      fullName: person.full_name,
      email: person.email || '',
      phone: person.phone || '',
      directPhone: person.direct_phone || '',
      zalo: person.zalo || '',
      languagesSpoken: person.languages || '',
      sortOrder: person.sort_order,
      isHighlight: person.is_highlight,
      isActive: person.is_active
    });

    this.imagePreviewUrl = this.getImageUrl(person.avatar);

    this.languageForms.vi.patchValue({
      position: person.position_translations_vi || person.position || '',
      areaCovered: person.area_covered_translations_vi || person.area_covered || '',
      bio: person.bio_translations_vi || person.bio || ''
    });
    this.languageForms.en.patchValue({
      position: person.position_translations_en || '',
      areaCovered: person.area_covered_translations_en || '',
      bio: person.bio_translations_en || ''
    });
    this.languageForms.zh.patchValue({
      position: person.position_translations_zh || '',
      areaCovered: person.area_covered_translations_zh || '',
      bio: person.bio_translations_zh || ''
    });
  }

  private getImageUrl(imagePath: string | null): string {
    if (!imagePath) {
      return '';
    }
    if (imagePath.indexOf('http') === 0) {
      return imagePath;
    }
    return environment.backendhost + imagePath;
  }

  private submitPerson(payload: ContactPersonRequestDTO): void {
    const person = this.data.person;
    const request$ =
      this.data.mode === 'edit' && person
        ? this.contactManagementDataService.updatePerson(person.id, payload)
        : this.contactManagementDataService.createPerson(payload);

    this.isSaving = true;

    request$
      .pipe(
        take(1),
        switchMap((response) => {
          if (!response || !response.success) {
            return of(response);
          }
          if (!this.selectedImageFile) {
            return of(response);
          }
          const personId =
            person && person.id
              ? person.id
              : response.data && response.data.id
                ? response.data.id
                : '';
          if (!personId) {
            return throwError({
              isImageUploadError: true,
              message: 'Nhân sự đã được lưu nhưng không tìm thấy ID để tải ảnh'
            });
          }
          return this.contactManagementDataService
            .uploadPersonImage(personId, this.selectedImageFile)
            .pipe(
              take(1),
              switchMap((uploadResponse) => {
                if (!uploadResponse || !uploadResponse.success) {
                  return throwError({
                    isImageUploadError: true,
                    message:
                      uploadResponse && uploadResponse.message
                        ? uploadResponse.message
                        : 'Nhân sự đã được lưu nhưng không thể tải ảnh'
                  });
                }
                return of(response);
              }),
              catchError((error) => {
                if (error && error.isImageUploadError) {
                  return throwError(error);
                }
                const errorResponse = error && error.error ? error.error : null;
                return throwError({
                  isImageUploadError: true,
                  message:
                    errorResponse && errorResponse.message
                      ? errorResponse.message
                      : 'Nhân sự đã được lưu nhưng không thể tải ảnh'
                });
              })
            );
        }),
        finalize(() => {
          this.isSaving = false;
        })
      )
      .subscribe(
        (response) => {
          if (!response || !response.success) {
            this.toastr.error(
              response && response.message ? response.message : 'Không thể lưu nhân sự'
            );
            return;
          }
          const isEditing = this.data.mode === 'edit';
          this.toastr.success(isEditing ? 'Cập nhật nhân sự thành công' : 'Tạo nhân sự thành công');
          this.dialogRef.close({ created: !isEditing, updated: isEditing });
        },
        (error) => {
          if (error && error.isImageUploadError) {
            this.toastr.error(error.message);
            return;
          }
          const errorResponse = error && error.error ? error.error : null;
          let message = 'Không thể lưu nhân sự';
          if (errorResponse && errorResponse.message) {
            message = errorResponse.message;
          } else if (errorResponse && errorResponse.title) {
            message = errorResponse.title;
          }
          this.toastr.error(message);
        }
      );
  }
}
